import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { normalizeSkills } from "@/utils/skillNormalization";

interface JobSkillSummary {
  skill_name: string;
  usage_count: number;
  organization_count: number;
  skill_sources: string[];
}

export const useJobSkillsFilter = () => {
  const [skills, setSkills] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobSkills = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use the secure view which filters by user's organization access
        const { data, error } = await supabase
          .from('job_skills_summary_secure')
          .select('skill_name, usage_count, skill_sources')
          .order('usage_count', { ascending: false })
          .order('skill_name', { ascending: true });
        
        if (error) {
          throw error;
        }

        if (data) {
          // Extract just the skill names for the dropdown
          const skillNames = data.map((row: JobSkillSummary) => row.skill_name);
          setSkills(skillNames);
        }
      } catch (err) {
        console.error('Error fetching job skills from materialized view:', err);
        setError(err instanceof Error ? err.message : 'Failed to load job skills');
        
        // Fallback: Try to fetch skills the old way if materialized view fails
        try {
          console.log('Falling back to direct job skills extraction...');
          const { data: jobsData, error: fallbackError } = await supabase
            .from('jobs')
            .select('skills, hard_skills, soft_skills')
            .or('skills.not.is.null,hard_skills.not.is.null,soft_skills.not.is.null');

          if (fallbackError) throw fallbackError;

          const allSkillsArray: string[] = [];
          jobsData.forEach(job => {
            // Add skills from all three arrays
            if (job.skills) {
              job.skills.forEach((skill: string) => {
                if (skill?.trim()) {
                  allSkillsArray.push(skill.trim());
                }
              });
            }
            if (job.hard_skills) {
              job.hard_skills.forEach((skill: string) => {
                if (skill?.trim()) {
                  allSkillsArray.push(skill.trim());
                }
              });
            }
            if (job.soft_skills) {
              job.soft_skills.forEach((skill: string) => {
                if (skill?.trim()) {
                  allSkillsArray.push(skill.trim());
                }
              });
            }
          });

          // Use the same normalization logic as the database
          const fallbackSkills = normalizeSkills(allSkillsArray);
          setSkills(fallbackSkills);
          setError(null); // Clear error since fallback worked
        } catch (fallbackErr) {
          console.error('Fallback job skills fetch also failed:', fallbackErr);
          setError('Failed to load job skills');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobSkills();
  }, []);

  return { skills, isLoading, error };
};
